import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, FileText, ExternalLink, CheckCircle2 } from 'lucide-react';

const TermsModal = ({ isOpen, onClose, onAccept, orderTotal }) => {
  const { t, i18n } = useTranslation();
  const [accepted, setAccepted] = useState(false);
  const isSpanish = i18n.language === 'es';

  if (!isOpen) return null;

  const handleAccept = () => {
    if (accepted) {
      onAccept();
    }
  };

  const keyTerms = isSpanish ? [
    'Todas las ventas de productos personalizados son FINALES — no hay reembolsos una vez aprobado el diseño',
    'Tienes 1 hora después de la aprobación del diseño para solicitar cambios',
    'Eres responsable de la calidad y precisión de tu diseño enviado',
    'Se requiere el 100% del pago antes de iniciar la producción',
    'El riesgo de envío pasa a ti una vez que el paquete llega al transportista',
    'Los tiempos de producción son estimados y pueden variar'
  ] : [
    'All custom product sales are FINAL — no refunds once artwork is approved',
    'You have 1 hour after artwork approval to request any changes',
    'You are responsible for the quality and accuracy of your submitted artwork',
    '100% payment is required before production begins',
    'Shipping risk passes to you once the package reaches the carrier',
    'Production times are estimated and may vary'
  ];

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="bg-[#1A3A5C] px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-lg">ONE2TEES</h2>
              <p className="text-white/70 text-xs">
                {isSpanish ? 'Términos y Condiciones' : 'Terms & Conditions'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Order Total */}
          {orderTotal && (
            <div className="bg-slate-50 rounded-lg p-3 mb-4 flex justify-between items-center">
              <span className="text-sm text-slate-600">
                {isSpanish ? 'Total de tu pedido:' : 'Your order total:'}
              </span>
              <span className="text-lg font-bold text-[#1A3A5C]">${orderTotal.toFixed(2)}</span>
            </div>
          )}

          {/* Key Terms Summary */}
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-slate-800 mb-3">
              {isSpanish ? 'Puntos Importantes:' : 'Key Points:'}
            </h3>
            <ul className="space-y-2">
              {keyTerms.map((term, index) => (
                <li key={index} className="flex items-start gap-2 text-sm text-slate-600">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#1A3A5C] mt-1.5 flex-shrink-0" />
                  {term}
                </li>
              ))}
            </ul>
          </div>

          {/* Scrollable Full Terms */}
          <div className="border border-slate-200 rounded-lg mb-4">
            <button 
              className="w-full px-4 py-2 flex items-center justify-between text-sm text-slate-600 hover:bg-slate-50 transition-colors"
              onClick={() => window.open('/terms', '_blank')}
            >
              <span className="flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {isSpanish ? 'Ver Términos Completos' : 'View Full Terms'}
              </span>
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>

          {/* Checkbox */}
          <label className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => setAccepted(e.target.checked)}
              className="mt-0.5 w-5 h-5 rounded border-2 border-[#1A3A5C] text-[#1A3A5C] focus:ring-[#1A3A5C] cursor-pointer"
              data-testid="terms-modal-checkbox"
            />
            <span className="text-sm text-slate-700 leading-relaxed">
              {isSpanish 
                ? 'He leído y acepto los Términos y Condiciones de ONE2TEES. Entiendo que todas las ventas son finales.'
                : 'I have read and agree to the ONE2TEES Terms and Conditions. I understand that all sales are final.'}
            </span>
          </label>
        </div>

        {/* Footer Buttons */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
            data-testid="terms-modal-cancel"
          >
            {isSpanish ? 'Cancelar' : 'Cancel'}
          </button>
          <button
            onClick={handleAccept}
            disabled={!accepted}
            className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
              accepted 
                ? 'bg-[#1A3A5C] text-white hover:bg-[#152e4a]' 
                : 'bg-slate-200 text-slate-400 cursor-not-allowed'
            }`}
            data-testid="terms-modal-accept"
          >
            {accepted && <CheckCircle2 className="w-4 h-4" />}
            {isSpanish ? 'Acepto — Completar Pedido' : 'I Agree — Complete Order'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
